import { match } from 'path-to-regexp';
import { generatePath } from 'react-router-dom';
import {
  BASE_ROUTE,
  PATHS,
  SAMPLE_PROJECT_ACCOUNT_ID,
  TEMPLATES_PATH,
} from 'routes/routes.constants';
import {
  BRANCH_RESOURCE_SET,
  PLG_RESOURCES,
  PROJECT_RESOURCE_PATH_MAP,
} from 'routes/routesRegistry';

import type { AssistantType } from 'api/types';
import {
  BUILDER_NAVIGATION_RESOURCES,
  MANAGER_NAVIGATION_RESOURCES,
} from 'components/organisms/ProjectNavigation/ProjectNavigation.constants';
import type { FeatureFlag } from 'hooks/useFeatureSwitch/useIsFeatureOn';
import {
  PROJECT_RESOURCE,
  type ProjectResource,
} from 'hooks/useProjectResources/constants';
import { hasFeatureAccess } from 'lib/featuresHelper';
import { ResponseControl } from 'screens/ResponseControl/types';
import { SpeechRecognition } from 'screens/SpeechRecognition/types';
import {
  getStandardDashboardDefaultEndDate,
  getStandardDashboardDefaultStartDate,
} from 'screens/StandardDashboard/helpers';

export const matchNextPath = (
  currentUrl: string,
  expectedTemplate: string,
  exact: boolean = true,
) => {
  const matchFn = match(
    expectedTemplate.replace(/\[\w+\]/g, m => `:${m.slice(1, -1)}`),
    { decode: decodeURIComponent, end: exact },
  );

  return !!matchFn(currentUrl.split('?')[0]);
};

export const generateNextPath = <Path extends string>(
  template: Path,
  urlParams?: Parameters<typeof generatePath<Path>>[1],
  queryParams?: { [key: string]: string | undefined },
) => {
  const filteredQuery = queryParams
    ? Object.keys(queryParams).reduce(
        (filt, key) =>
          queryParams[key] ? { ...filt, [key]: queryParams[key]! } : filt,
        {} as { [key: string]: string },
      )
    : {};

  const params = urlParams as Record<string, string> | undefined;
  const isInTemplatePreview =
    typeof window !== 'undefined' &&
    window.location.pathname.startsWith(`${TEMPLATES_PATH}/`);
  const shouldRewriteToTemplates =
    isInTemplatePreview &&
    template.startsWith('/') &&
    template.includes(':accountId') &&
    (params?.accountId === SAMPLE_PROJECT_ACCOUNT_ID || !params?.accountId);

  let finalTemplate: string = template;
  let finalParams = params;

  if (shouldRewriteToTemplates) {
    finalTemplate = template.replace('/:accountId/', `${TEMPLATES_PATH}/`);

    if (finalParams) {
      const { accountId: _, ...rest } = finalParams;
      finalParams = rest as typeof params;
    }
  }

  if (!Object.keys(filteredQuery).length) {
    return generatePath(finalTemplate, finalParams);
  }
  const query = queryParams ? new URLSearchParams(filteredQuery) : undefined;

  return `${generatePath(finalTemplate, finalParams)}?${query?.toString()}`;
};

export const generateProjectLandingResource = async (
  accountId: string,
  projectId: string,
  projectType: AssistantType,
  isSampleProject: boolean = false,
  isFeatureOn: (feature: FeatureFlag) => boolean = () => true,
  branchId?: string,
  isTemplateEngineProject: boolean = false,
  isPlgProd: boolean = false,
  isFirstLogin: boolean = false,
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: existing
) => {
  const isHomePageEnabled = isFeatureOn('home_page');

  const { default: api } = await import('api');

  let data;

  if (isSampleProject) {
    return generateNextPath(PATHS.AGENT, {
      accountId,
      projectId,
    });
  }
  if (isFeatureOn('agentic_assistant') && isFirstLogin) {
    return generateNextPath(PATHS.STUDIO_ASSISTANT_ONBOARDING, {
      accountId,
      projectId,
    });
  }
  if (isTemplateEngineProject) {
    if (isHomePageEnabled) {
      return generateNextPath(PATHS.ANALYTICS_HOME, {
        accountId,
        projectId,
      });
    }
    return generateNextPath(PATHS.SITES, {
      accountId,
      projectId,
    });
  }
  data = await api.getEnabledFeatures(accountId, projectId);

  // if no resource found (for which project wouldn't be visible in the first place),
  // fallback to knowledge base where permission will disallow access anyway
  const enabledResources = (
    Object.keys(PROJECT_RESOURCE_PATH_MAP) as ProjectResource[]
  ).filter(
    resource =>
      (!isPlgProd || PLG_RESOURCES.includes(resource)) &&
      hasFeatureAccess(
        resource,
        data.features,
        data.restricted_features,
        projectType,
      ) &&
      (!branchId || BRANCH_RESOURCE_SET.has(resource)),
  );

  let firstEnabledResource = enabledResources[0] || PROJECT_RESOURCE.ANALYTICS;
  if (firstEnabledResource === PROJECT_RESOURCE.HOME && !isHomePageEnabled) {
    firstEnabledResource = PROJECT_RESOURCE.ANALYTICS;
  }

  // If the first enabled resource is one of the manager navigation resources,
  // check if there are any conversations in the standard data.
  // If there are no conversations, fallback to the first resource that is not a
  // manager navigation resource.
  if (MANAGER_NAVIGATION_RESOURCES.includes(firstEnabledResource)) {
    try {
      const standardData = await api.getStandardisedData(
        accountId,
        projectId,
        'daily',
        getStandardDashboardDefaultStartDate().toISOString(),
        getStandardDashboardDefaultEndDate().toISOString(),
      );
      if (standardData.total.num_conversations === 0) {
        const firstBuilderResource = (
          Object.keys(PROJECT_RESOURCE_PATH_MAP) as ProjectResource[]
        ).find(
          resource =>
            BUILDER_NAVIGATION_RESOURCES.includes(resource) &&
            hasFeatureAccess(
              resource,
              data.features,
              data.restricted_features,
              projectType,
            ),
        );

        // If there is no builder resource available, fallback to the first enabled
        // resource
        if (firstBuilderResource) {
          firstEnabledResource = firstBuilderResource;
        }
      }
    } catch (_e) {}
  }

  const pathUrlParams = {
    accountId,
    projectId,
    dashboardSlug: null,
    conversationId: null,
    type: null,
    flowId: null,
    functionId: null,
    transcriptCorrectionId: null,
    stopKeywordId: null,
    overwriteRuleId: null,
    fileId: null,
    taskId: null,
    testCaseId: null,
    testSetId: null,
    clientEnv: null,
    batchId: null,
    scheduleId: null,
    chatId: null,
    apiId: null,
    siteId: null,
  };

  if (firstEnabledResource === PROJECT_RESOURCE.CUSTOM_ANALYTICS) {
    const customIntegration = await api.getCustomIntegration(
      accountId,
      projectId,
    );

    if (customIntegration?.embed_dashboards?.length) {
      return generateNextPath(
        PROJECT_RESOURCE_PATH_MAP[PROJECT_RESOURCE.CUSTOM_ANALYTICS]!,
        {
          ...pathUrlParams,
          dashboardSlug: customIntegration?.embed_dashboards[0].slug || null,
        },
        {
          branchId,
        },
      );
    }
    firstEnabledResource = enabledResources[1] || PROJECT_RESOURCE.ANALYTICS;
  }

  if (firstEnabledResource === PROJECT_RESOURCE.REALTIME_CONFIG_MANAGER) {
    // This route requires :clientEnv which isn't available at landing time
    firstEnabledResource =
      enabledResources.find(
        r => r !== PROJECT_RESOURCE.REALTIME_CONFIG_MANAGER,
      ) || PROJECT_RESOURCE.ANALYTICS;
  }

  if (firstEnabledResource === PROJECT_RESOURCE.AGENT_CONFIG) {
    const res = await api.getAgentConfigFiles(accountId, projectId);

    if (!res?.files?.length) {
      return `${PROJECT_RESOURCE_PATH_MAP[firstEnabledResource]}`;
    }

    return generateNextPath(
      PROJECT_RESOURCE_PATH_MAP[firstEnabledResource]!,
      {
        ...pathUrlParams,
        fileId: res.files[0].id,
      },
      {
        branchId,
      },
    );
  }

  // When mergedKnowledge is enabled, redirect to /knowledge/managed instead of /knowledge-base
  if (
    firstEnabledResource === PROJECT_RESOURCE.KNOWLEDGE_BASE &&
    isFeatureOn('mergedKnowledge')
  ) {
    return generateNextPath(
      PATHS.KNOWLEDGE,
      {
        ...pathUrlParams,
        type: 'managed',
      },
      {
        branchId,
      },
    );
  }

  return generateNextPath(
    PROJECT_RESOURCE_PATH_MAP[firstEnabledResource]!,
    {
      ...pathUrlParams,
      type:
        firstEnabledResource === PROJECT_RESOURCE.SPEECH_RECOGNITION
          ? SpeechRecognition.KEYPHRASE_BOOSTING
          : ResponseControl.STOP_KEYWORDS,
    },
    {
      branchId,
    },
  );
};

export const stripProjectRoute = (path: string) => path.replace(BASE_ROUTE, '');

export const generateAtlasURL = (): string => {
  return 'https://atlas.us-1.platform.private.polyai.app/annotator/annotation/annotation_task-059b34b2-bbd7-426c-80d3-dee3a5da1c5e/annotation-411df6c0-8813-4fc2-ad07-00250d5b8cd7-0';
};
